import { useMemo } from "react";
import { useAuthStore } from "@/store/useAuthStore";
import { PermissionName, FEATURE_PERMISSIONS, ROUTE_PERMISSIONS } from "@/constants/permissions";

/**
 * Custom hook for checking user permissions
 * Provides utilities to check if user has specific permissions
 */
export function usePermissions() {
  const { user } = useAuthStore();
  const userPermissions = user?.permissionNames || [];

  /**
   * Check if user has a specific permission
   */
  const hasPermission = useMemo(
    () => (permission: PermissionName): boolean => {
      return userPermissions.includes(permission);
    },
    [userPermissions]
  );

  /**
   * Check if user has ALL of the specified permissions
   */
  const hasAllPermissions = useMemo(
    () => (permissions: PermissionName[]): boolean => {
      return permissions.every((permission) => userPermissions.includes(permission));
    },
    [userPermissions]
  );

  /**
   * Check if user has ANY of the specified permissions
   */
  const hasAnyPermission = useMemo(
    () => (permissions: PermissionName[]): boolean => {
      return permissions.some((permission) => userPermissions.includes(permission));
    },
    [userPermissions]
  );

  /**
   * Check if user can access a specific route
   */
  const canAccessRoute = useMemo(
    () => (route: string): boolean => {
      const requiredPermissions = ROUTE_PERMISSIONS[route as keyof typeof ROUTE_PERMISSIONS];
      if (!requiredPermissions) return true; // No permissions required
      return hasAnyPermission(requiredPermissions as unknown as PermissionName[]);
    },
    [hasAnyPermission]
  );

  /**
   * Check if user can access a specific feature
   */
  const canAccessFeature = useMemo(
    () => (feature: keyof typeof FEATURE_PERMISSIONS): boolean => {
      const requiredPermissions = FEATURE_PERMISSIONS[feature];
      if (!requiredPermissions) return true;
      return hasAllPermissions(requiredPermissions as unknown as PermissionName[]);
    },
    [hasAllPermissions]
  );

  /**
   * Get all user permissions
   */
  const getAllPermissions = useMemo(() => userPermissions, [userPermissions]);

  /**
   * Check if user has no permissions (for debugging)
   */
  const hasNoPermissions = useMemo(() => userPermissions.length === 0, [userPermissions]);

  return {
    // Permission checking functions
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
    canAccessRoute,
    canAccessFeature,
    permissions: getAllPermissions,
    hasNoPermissions,
  };
}

/**
 * Helper function to check permissions outside of React components
 */
export function checkPermission(userPermissions: string[], permission: PermissionName): boolean {
  return userPermissions.includes(permission);
}

/**
 * Helper function to check multiple permissions outside of React components
 */
export function checkAllPermissions(userPermissions: string[], permissions: PermissionName[]): boolean {
  return permissions.every((permission) => userPermissions.includes(permission));
}

/**
 * Helper function to check if user has any of the permissions outside of React components
 */
export function checkAnyPermission(userPermissions: string[], permissions: PermissionName[]): boolean {
  return permissions.some((permission) => userPermissions.includes(permission));
}
